import { useEffect } from "react";
import { logger } from "../../utils/logger";

export function useMeshAppearance({ sceneObjects, meshOpacity, meshFlatShading }) {
  // Three.js's built-in Object3D.traverse() will overflow the call stack if the scene graph
  // accidentally contains a cycle (can happen if a body is re-parented under its descendant).
  // Use an iterative, cycle-safe traversal to avoid crashing the app.
  const safeTraverse = (root, visitor) => {
    if (!root || typeof visitor !== "function") return;
    const stack = [root];
    const seen = new Set();
    while (stack.length > 0) {
      const obj = stack.pop();
      if (!obj || seen.has(obj)) continue;
      seen.add(obj);
      try {
        visitor(obj);
      } catch (e) {
        // Don't let a single bad visitor crash the whole traversal.

        logger.warn("[useMeshAppearance] Visitor error during traversal:", e);
      }
      const children = obj.children;
      if (Array.isArray(children) && children.length > 0) {
        for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
      }
    }
  };

  // Opacity overrides
  useEffect(() => {
    Object.values(sceneObjects).forEach((obj) => {
      safeTraverse(obj, (child) => {
        const meshId = child?.userData?.meshName;
        if (
          child.isMesh &&
          child.material &&
          meshId &&
          Object.prototype.hasOwnProperty.call(meshOpacity, meshId)
        ) {
          child.material.transparent = true;
          child.material.opacity = meshOpacity[meshId];
          child.material.needsUpdate = true;
        }
      });
    });
  }, [meshOpacity, sceneObjects]);

  // Flat shading per-mesh
  useEffect(() => {
    Object.values(sceneObjects).forEach((obj) => {
      safeTraverse(obj, (child) => {
        const meshId = child?.userData?.meshName;
        if (
          child.isMesh &&
          child.material &&
          meshId &&
          Object.prototype.hasOwnProperty.call(meshFlatShading, meshId)
        ) {
          const isFlat = meshFlatShading[meshId];
          if (
            child.material.type === "MeshStandardMaterial" ||
            child.material.type === "MeshPhongMaterial"
          ) {
            const oldFlat = child.material.flatShading;
            if (oldFlat !== isFlat) {
              child.material.flatShading = isFlat;
              if (!isFlat && child.geometry && child.geometry.computeVertexNormals) {
                child.geometry.computeVertexNormals();
                child.geometry.normalsNeedUpdate = true;
              }
              child.material.needsUpdate = true;
            } else if (!isFlat && child.geometry && child.geometry.computeVertexNormals) {
              // Ensure smooth normals when value unchanged but geometry may need recompute
              child.geometry.computeVertexNormals();
              child.geometry.normalsNeedUpdate = true;
              child.material.needsUpdate = true;
            }
          }
        }
      });
    });
  }, [meshFlatShading, sceneObjects]);
}
